<template>
  <BaseModal
    :title="$i18next.t('message.ai.subtitles.subtitle')"
    hookSufix="SubtitleContent"
    modalWidth="1024px"
    :noDefaultPadding="true"
    :smallToolbarPadding="true"
    :onOpenHook="handleCheckOpen"
    :onCloseHook="handleResetClose"
    :persistent="loadings.updatingSubtitle"
  >
    <template #content>
      <v-container class="pa-0 mb-6 d-flex container__wrapper px-6 flex-column">
        <v-row no-gutters class="container__wrapper--sub">
          <v-col class="mr-1">
            <div class="d-flex justify-space-between mb-4">
              <v-select
                v-model="currentSubtitle"
                class="v-input__custom-colorized"
                style="max-width: 200px"
                outlined
                hide-details
                :placeholder="
                  $i18next.t('message.ai.subtitles.selectASubtitle')
                "
                item-text="label"
                item-value="srclang"
                :value="currentSubtitle"
                :menu-props="{ top: false, offsetY: true }"
                :class="{
                  dark: $vuetify.theme.dark,
                }"
                :no-data-text="$t('message.pages.videoEditModal.usedSubtitles')"
                :items="subtitles"
                dense
              >
                <template #prepend-inner v-if="currentSubtitle.length">
                  <country-flag
                    :country="
                      getCountryCodeBySrcLang(
                        getPreparedSrcLang(currentSubtitle)
                      )
                    "
                  />
                </template>
                <template #append>
                  <v-icon> mdi-chevron-down </v-icon>
                </template>
              </v-select>

              <div class="d-flex align-center">
                <span
                  class="text-subtitle-1 font-weight-regular mr-3"
                  :class="{
                    'neutral--text': !$vuetify.theme.dark,
                    'white--text': $vuetify.theme.dark,
                  }"
                  >{{ $i18next.t('message.pages.subtitleEditor.delay') }}</span
                >
                <div
                  class="d-flex align-center justify-space-between container__delay"
                >
                  <v-icon
                    color="brand"
                    size="32"
                    class="mr-2"
                    @click="handleDecreaseDelay"
                  >
                    mdi-chevron-left
                  </v-icon>
                  <span
                    class="text-subtitle-1 font-weight-regular"
                    :class="{
                      'neutral--text': !$vuetify.theme.dark,
                      'white--text': $vuetify.theme.dark,
                    }"
                    >{{ delayAmount.toFixed(1) }} s</span
                  >
                  <v-icon
                    color="brand"
                    size="32"
                    class="ml-2"
                    @click="handleIncreaseDelay"
                  >
                    mdi-chevron-right
                  </v-icon>
                </div>
              </div>
            </div>

            <div
              class="container__vtt-list"
              no-gutters
              v-if="parsedSubtitleTableItems"
            >
              <vue-virtual-table
                v-if="parsedSubtitleTableItems.length > 0"
                ref="dataTableContent"
                class="data-table__content"
                :class="{ virtualTable: $vuetify.theme.dark }"
                :config="tableConfig"
                :data="parsedSubtitleTableItems"
                :height="636"
                :minWidth="476"
                :itemHeight="60"
                :bordered="false"
                :hoverHighlight="false"
                :plusHeight="30"
                style="position: relative; z-index: 1"
              >
                <template #before-content>
                  <ProgressLine class="progress-line" ref="progressLine" />
                </template>
                <template slot-scope="scope" slot="rowCommon">
                  <div class="subtitle-item" :title="scope.row.height">
                    <div
                      class="d-flex align-start pa-2"
                      :class="{
                        'selected-item': scope.row.selected,
                      }"
                    >
                      <div class="d-flex align-center">
                        <v-icon
                          v-if="scope.row.metadata.locked"
                          class="neutral--text text--lighten-2 mr-3 lock-btn"
                          @click="handleToggleLockState(scope.index)"
                          @keydown.prevent
                          @keypress.prevent
                          @keyup.prevent
                          size="16px"
                        >
                          mdi-lock
                        </v-icon>
                        <v-icon
                          v-else
                          class="neutral--text text--lighten-2 mr-3 lock-btn unlocked"
                          @click="handleToggleLockState(scope.index)"
                          @keydown.prevent
                          @keypress.prevent
                          @keyup.prevent
                          size="16px"
                        >
                          mdi-lock-open-variant
                        </v-icon>
                        <div class="d-flex flex-column container__time-info">
                          <div
                            class="d-flex font-weight-medium text-subtitle-2 time-field align-end pointer"
                            :class="{
                              'neutral--text':
                                scope.row.metadata.fieldEdit.index === 1,
                              'brand--text': scope.row.metadata.selected,
                            }"
                            @click="handleEditField(scope.index, 1)"
                          >
                            <span
                              v-if="isLongerThanAnHour"
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 1, 'hours')
                              "
                              @click="
                                handleEditFieldType(scope.index, 1, 'hours')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.startTime)[0]
                              }}:</span
                            ><span
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 1, 'minutes')
                              "
                              @click="
                                handleEditFieldType(scope.index, 1, 'minutes')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.startTime)[1]
                              }}</span
                            >:<span
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 1, 'seconds')
                              "
                              @click="
                                handleEditFieldType(scope.index, 1, 'seconds')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.startTime)[2]
                              }}</span
                            >
                            &nbsp;<span
                              class="font-weight-regular text-caption time-field"
                              :class="
                                getTimeFieldClasses(
                                  scope.row,
                                  1,
                                  'milliseconds'
                                )
                              "
                              @click="
                                handleEditFieldType(
                                  scope.index,
                                  1,
                                  'milliseconds'
                                )
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.startTime)[3]
                              }}</span
                            >
                          </div>
                          <div
                            class="font-weight-medium text-subtitle-2 time-field align-end pointer"
                            :class="{
                              'neutral--text':
                                scope.row.metadata.fieldEdit.index === 2,
                              'brand--text': scope.row.metadata.selected,
                            }"
                            @click="handleEditField(scope.index, 2)"
                          >
                            <span
                              v-if="isLongerThanAnHour"
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 2, 'hours')
                              "
                              @click="
                                handleEditFieldType(scope.index, 2, 'hours')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.endTime)[0]
                              }}:</span
                            >
                            <span
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 2, 'minutes')
                              "
                              @click="
                                handleEditFieldType(scope.index, 2, 'minutes')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.endTime)[1]
                              }}</span
                            >:<span
                              class="time-field"
                              :class="
                                getTimeFieldClasses(scope.row, 2, 'seconds')
                              "
                              @click="
                                handleEditFieldType(scope.index, 2, 'seconds')
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.endTime)[2]
                              }}</span
                            >&nbsp;<span
                              class="font-weight-regular text-caption time-field"
                              :class="
                                getTimeFieldClasses(
                                  scope.row,
                                  2,
                                  'milliseconds'
                                )
                              "
                              @click="
                                handleEditFieldType(
                                  scope.index,
                                  2,
                                  'milliseconds'
                                )
                              "
                              >{{
                                getSecondsTimeFormatted(scope.row.endTime)[3]
                              }}</span
                            >
                          </div>
                        </div>

                        <div
                          class="d-flex flex-column mr-2 align-self-center align-content-space-between"
                          style="margin-left: 3px; min-width: 16px"
                        >
                          <template v-if="scope.row.metadata.fieldEdit.index">
                            <v-icon
                              color="brand"
                              size="16px"
                              class="pointer"
                              @mousedown="handleAddToEditField(scope.index)"
                              @mouseup="handleBlurEditField()"
                              >mdi-triangle</v-icon
                            >
                            <v-icon
                              color="brand"
                              size="16px"
                              class="mt-1 pointer"
                              @mousedown="handleSubFromEditField(scope.index)"
                              @mouseup="handleBlurEditField()"
                              >mdi-triangle-down</v-icon
                            >
                          </template>
                        </div>
                      </div>
                      <span
                        v-if="!scope.row.metadata.edit"
                        class="text-subtitle-2 font-weight-regular time-content pointer"
                        :class="{ 'neutral--text': !$vuetify.theme.dark }"
                        @click="handleEnableEditContentMode(scope.index)"
                        v-html="scope.row.text"
                      ></span>
                      <v-textarea
                        v-else
                        v-model="textareaTexts[scope.index]"
                        :value="parsedSubtitle[scope.index].text"
                        outlined
                        class="v-text-field__custom-colorized v-text-field__edit-subtitle scroll--stylized"
                        :placeholder="
                          $i18next.t(
                            'message.ai.dubbings.modal.auto.step_four.textarea'
                          )
                        "
                        :no-resize="true"
                        height="66px"
                        autofocus
                        hide-details
                        @keydown.enter="editCurrentSub"
                      ></v-textarea>
                      <div class="d-flex align-center px-2">
                        <template v-if="scope.row.metadata.edit">
                          <BaseBtn
                            @click="handleDisableEditContentMode(scope.index)"
                            size="small"
                            >Concluir</BaseBtn
                          >
                        </template>
                        <template v-else>
                          <v-menu
                            v-model="menu[scope.index]"
                            content-class="v-menu__tooltip"
                            :close-on-content-click="false"
                            :right="true"
                            :nudge-right="35"
                            :nudge-top="20"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                                size="24px"
                                :color="
                                  scope.row.metadata.selected
                                    ? 'var(--v-brand-lighten1)'
                                    : 'transparent'
                                "
                                class="mr-1 delete-subtitle"
                                v-bind="attrs"
                                v-on="on"
                                @click="
                                  menu[scope.index] = !Boolean(
                                    menu[scope.index]
                                  )
                                "
                              >
                                mdi-close-circle
                              </v-icon>
                            </template>

                            <v-card :class="$vuetify.theme.dark ? 'white' : ''">
                              <span
                                class="text-subtitle-1 neutral--text mr-6 font-weight-regular"
                                >{{
                                  $i18next.t(
                                    'message.pages.subtitleEditor.deleteSub'
                                  )
                                }}</span
                              >
                              <BaseBtn
                                color="red"
                                @click="deleteSubtitlePart(scope.index)"
                                >{{
                                  $i18next.t(
                                    'message.pages.subtitleEditor.delete'
                                  )
                                }}</BaseBtn
                              >
                            </v-card>
                          </v-menu>
                          <v-icon
                            size="24px"
                            :color="
                              scope.row.metadata.selected
                                ? 'brand'
                                : 'transparent'
                            "
                            @click="handlePlaySelectedSubtitle(scope.index)"
                            class="ml-1 play-subtitle"
                          >
                            mdi-play
                          </v-icon>
                        </template>
                      </div>
                    </div>
                    <div
                      class="text-center brand lighten-3 duplicate-subtitle d-none"
                      :class="{
                        'd-block': scope.row.metadata.selected,
                      }"
                    >
                      <v-icon
                        color="brand"
                        size="16px"
                        class="transparent"
                        @click="duplicateSub(scope.index)"
                      >
                        mdi-plus
                      </v-icon>
                    </div>
                  </div>
                </template>
              </vue-virtual-table>
            </div>
          </v-col>
          <v-col class="ml-1 container__player-side">
            <div class="container__timeline">
              <div class="video__container">
                <video ref="videoTag"></video>
                <div
                  class="video__progress-bar pointer"
                  @click="handleSetNewVideoTime"
                >
                  <div class="video__progress-dot"></div>
                  <div class="video__progress-bar--remaining-space"></div>
                  <div class="video__progress-seek"></div>
                </div>
              </div>
              <div class="d-flex justify-space-between pa-4 pt-3">
                <span
                  class="text-subtitle-1 font-weight-regular"
                  :class="{
                    'neutral--text': !$vuetify.theme.dark,
                    'white--text': $vuetify.theme.dark,
                  }"
                  style="width: 95px"
                  >{{
                    formatTime(
                      videoProperties.currentTime,
                      `${isLongerThanAnHour ? 'HH:' : ''}mm:ss`
                    )
                  }}
                  <small>{{
                    videoProperties.currentTime.toFixed(2).slice(-2)
                  }}</small></span
                >
                <div class="d-flex align-center" style="margin-left: -25px">
                  <v-icon
                    :color="$vuetify.theme.dark ? 'white' : 'neutral'"
                    size="16px"
                    @click="handleBackwardVideo"
                  >
                    mdi-rewind-5
                  </v-icon>
                  <v-icon
                    :color="$vuetify.theme.dark ? 'white' : 'neutral'"
                    class="mx-4"
                    @click="handleTogglePlayVideo"
                    @keydown.prevent
                    @keypress.prevent
                    @keyup.prevent
                  >
                    {{ videoProperties.paused ? 'mdi-play' : 'mdi-pause' }}
                  </v-icon>
                  <v-icon
                    :color="$vuetify.theme.dark ? 'white' : 'neutral'"
                    size="16px"
                    @click="handleForwardVideo"
                  >
                    mdi-fast-forward-5
                  </v-icon>
                </div>
                <div class="d-flex align-center">
                  <v-icon
                    :color="$vuetify.theme.dark ? 'white' : 'neutral'"
                    class="mr-2"
                    @click="zoomIn"
                    :disabled="zoomRate >= 2.5"
                  >
                    mdi-magnify-plus-outline
                  </v-icon>
                  <v-icon
                    :color="$vuetify.theme.dark ? 'white' : 'neutral'"
                    @click="zoomOut"
                    :disabled="zoomRate <= 0.5"
                  >
                    mdi-magnify-minus-outline
                  </v-icon>
                </div>
              </div>
              <div
                class="container__images--wrapper scroll--stylized"
                ref="containerImagesWrapper"
                @wheel="handleWheelHorizontally"
              >
                <ProgressLine
                  class="progress-line"
                  ref="progressLineTimeline"
                  direction="vertical"
                />
                <div class="container__time-items"></div>
                <div class="container__subtitle-items"></div>
                <div class="container__images"></div>
              </div>
            </div>
            <div class="mt-4 container__shortcuts scroll--stylized">
              <div class="header">
                <span
                  class="neutral--text text--lighten-1 font-weight-medium"
                  >{{
                    $i18next.t('message.pages.subtitleEditor.shortcuts.header')
                  }}</span
                >
              </div>
              <v-row no-gutters class="list__shortcuts">
                <v-col cols="6">
                  <div class="container__shortcut-key">Espace</div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.play-pause'
                      )
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">
                    <v-icon style="transform: rotate(270deg)"
                      >mdi-triangle</v-icon
                    >
                  </div>
                  <div class="container__shortcut-key">
                    <v-icon style="transform: rotate(90deg)"
                      >mdi-triangle</v-icon
                    >
                  </div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.forward-backward'
                      )
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">K</div>
                  <div class="container__shortcut-key">L</div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t('message.pages.subtitleEditor.shortcuts.delay')
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle</v-icon>
                  </div>
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle-down</v-icon>
                  </div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.navigateSubs'
                      )
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">shift</div>
                  <v-icon
                    size="14px"
                    class="mx-1"
                    color="var(--v-neutral-lighten1)"
                    >mdi-plus</v-icon
                  >
                  <div class="container__shortcut-key">enter</div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.addSub'
                      )
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">Enter</div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t('message.pages.subtitleEditor.shortcuts.edit')
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">Shift</div>
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle</v-icon>
                  </div>
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle-down</v-icon>
                  </div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.editJoinTime'
                      )
                    }}</span
                  >
                </v-col>

                <v-col cols="6">
                  <div class="container__shortcut-key">Ctrl</div>
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle</v-icon>
                  </div>
                  <div class="container__shortcut-key">
                    <v-icon>mdi-triangle-down</v-icon>
                  </div>
                </v-col>
                <v-col cols="6">
                  <span
                    class="neutral--text text-subtitle-2 font-weight-regular"
                    >{{
                      $i18next.t(
                        'message.pages.subtitleEditor.shortcuts.editLeftTime'
                      )
                    }}</span
                  >
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template #beforeCloseBtn>
      <BaseBtn
        size="small"
        class="mr-6"
        @click="handleUpdateSubtitle"
        :disabled="loadings.updatingSubtitle"
        :loading="loadings.updatingSubtitle"
        >{{ $t('message.actions.save') }}</BaseBtn
      >
    </template>
  </BaseModal>
</template>

<script>
import VueVirtualTable from 'vue-virtual-table-variable'
import { mapState, mapMutations } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import Hls from 'hls.js'
import { format } from 'date-fns'
import axios from 'axios'
import { WebVTTParser, WebVTTSerializer } from 'webvtt-parser'
import CountryFlag from 'vue-country-flag'
import ProgressLine from '@/components/ProgressLine.vue'
import BaseModal from '@/components/BaseModal.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import videosApi from '@/services/videosApi'
import _ from 'lodash'

export default {
  name: 'SubtitleContentModal',
  mixins: [aiWorkflowMixin],
  components: {
    BaseModal,
    BaseBtn,
    CountryFlag,
    ProgressLine,
    VueVirtualTable,
  },
  data: () => ({
    isRunningTimeUpdate: false,
    textareaTexts: {},
    sheets: [],
    videoRatio: '16:9',
    lastMouseWheelEventTime: 0,
    allowAutoScroll: true,
    menu: [],
    currentSubtitle: '',
    subtitleContent: '',
    iframe: null,
    videoProperties: {
      paused: true,
      currentTime: 0,
    },
    parsedSubtitle: false,
    delayAmount: 0,
    clickTimeout: false,
    blockClickEvent: false,
    currentSubtitleObjectURL: '',
    loadings: {
      updatingSubtitle: false,
    },
    previewThumbURLs: [],
    pixelsPerSecond: 0,
    zoomRate: 1,
    updatingSubtitleContent: false,
    isTypingMode: false,
  }),
  beforeDestroy() {
    this.handleClearClickTimeout()
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('click', this.handleClickDOM)
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      subtitles: (state) => state.video.video.config.subtitles,
      profile: (state) => state.video.profile,
    }),
    isLongerThanAnHour() {
      const { endTime } = this.parsedSubtitle[this.parsedSubtitle.length - 1]
      return endTime >= 3600
    },
    parsedSubtitleTableItems() {
      if (!this.parsedSubtitle) return []
      const parsedSubtitleWithCalcHeight = _.cloneDeep(this.parsedSubtitle).map(
        (item) => {
          item.height = 60
          const lines = Math.floor(item.text.length / 38)
          item.text = this.removeHtmlTags(item.text)
          if (lines >= 2) {
            const ac = Math.ceil((item.text.length - 69) / 38)
            const lines = Math.ceil(item.text.length / 38)
            let newText = item.text
            for (let i = 1; i < lines; i++) {
              newText =
                newText.slice(0, i * 35) +
                '<br/>' +
                newText.slice(i * 35, newText.length)
            }
            item.text = newText
            item.height = 60 + 22 * ac
          }
          return item
        }
      )

      this.$root.$emit('callUpdateVisibleItems')
      return parsedSubtitleWithCalcHeight
    },
    tableConfig() {
      const tableConfig = [
        {
          prop: '_action',
          name: 'row',
          actionName: 'rowCommon',
          alignItems: 'start',
        },
      ]

      return tableConfig
    },
    selectedSubtitleSrcLang() {
      if (this.currentSubtitle) return this.currentSubtitle
      return false
    },
  },
  watch: {
    async selectedSubtitleSrcLang(val, oldVal) {
      if (val) {
        await this.handleOpenSubtitleContent(this.selectedSubtitleSrcLang)
        if (oldVal) this.handleUpdateSubtitleContent()
      }
    },
    subtitleContent() {
      this.setSubtitleContent()
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    removeHtmlTags(str) {
      return str.replace(/<\/?[^>]+(>|$)/g, '')
    },
    getTimeFieldClasses(item, fieldIndex, field) {
      let classes = {
        'neutral--text': false,
        'brand--text': false,
        'time-field__selected': false,
      }
      if (item.metadata.selected) {
        classes['brand--text'] = true
      }
      if (item.metadata.fieldEdit.index === fieldIndex) {
        classes['neutral--text'] = true

        if (item.metadata.fieldEdit.field === field) {
          classes['time-field__selected'] = true
        }
      } else if (item.metadata.fieldEdit.index !== 0 && item.metadata.locked) {
        if (item.metadata.fieldEdit.field === field) {
          classes['time-field__selected'] = true
        }
        classes['neutral--text'] = true
      }
      return classes
    },
    handleWheelHorizontally(e) {
      this.$refs.containerImagesWrapper.scrollLeft += e.deltaY
    },
    zoomIn() {
      if (this.zoomRate < 2.5) {
        this.zoomRate += 0.5
      }
      this.drawPreviewThumbSheet(true)
    },
    zoomOut() {
      if (this.zoomRate > 0.5) {
        this.zoomRate -= 0.5
      }
      this.drawPreviewThumbSheet(true)
    },
    setSubtitleContent() {
      if (this.subtitleContent) {
        const parser = new WebVTTParser()
        const tree = parser.parse(this.subtitleContent, 'metadata')
        this.parsedSubtitle = tree.cues
        // Set additional metadata
        this.parsedSubtitle = this.parsedSubtitle.map((item) => {
          return {
            ...item,
            metadata: {
              newText: '',
              edit: false,
              locked: true,
              fieldEdit: { index: 0, field: 'seconds' },
              id: uuidv4(),
            },
          }
        })
      }
    },
    duplicateSub(index) {
      let target = {
        id: '',
        startTime: Number.parseFloat(this.parsedSubtitle[index].startTime),
        endTime: Number.parseFloat(this.parsedSubtitle[index].endTime),
        pauseOnExit: false,
        direction: 'horizontal',
        snapToLines: true,
        linePosition: 'auto',
        lineAlign: 'start',
        textPosition: 'auto',
        positionAlign: 'auto',
        size: 100,
        alignment: 'center',
        metadata: {
          newText: '',
          edit: false,
          locked: true,
          fieldEdit: { index: 0, field: 'seconds' },
          id: uuidv4(),
        },
        text: String(this.parsedSubtitle[index].text),
        tree: {
          children: [
            {
              type: 'text',
              value: String(this.parsedSubtitle[index].text),
            },
          ],
        },
      }
      this.parsedSubtitle.splice(index, 0, target)
      this.drawPreviewThumbSheet()
    },
    async handleUpdateSubtitle() {
      this.loadings.updatingSubtitle = true
      const data = new FormData()
      const newSubtitlelabel = this.langCodes.filter(
        (item) => item.srclang === this.selectedSubtitleSrcLang
      )[0].label
      const fileVtt = this.createFileForUpload()
      data.append('file', fileVtt)
      data.append('label', newSubtitlelabel)
      data.append('srclang', this.selectedSubtitleSrcLang)
      try {
        await videosApi.updateVideoSubtitleContent(this.video.id, data)

        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$i18next.t('message.changesSaved'),
        })
      } catch (e) {
        console.error(e)
      }
      this.loadings.updatingSubtitle = false
    },
    updateSubtitleStyles(color, backgroundColor) {
      const styleId = 'dynamic-cue-styles'

      let styleTag = document.getElementById(styleId)

      if (!styleTag) {
        styleTag = document.createElement('style')
        styleTag.id = styleId
        document.head.appendChild(styleTag)
      }
      styleTag.textContent = `
            video::cue {
              background-color: ${backgroundColor ? backgroundColor : 'black'};
              color: ${color ? color : 'white'};
              line-height: 150%;
              font-size: 16px;
              font-family: Rubik;
            }
        `
    },
    addCustomSubtitleTrack(
      trackSrc,
      trackLabel = 'Portuguese',
      trackLang = 'pt_preview'
    ) {
      const videoElement = this.$refs.videoTag
      const track = document.createElement('track')
      track.kind = 'subtitles'
      track.label = trackLabel
      track.srclang = trackLang
      track.setAttribute('data-group-id', 'previewSubs')
      track.src = trackSrc
      track.mode = 'showing'

      videoElement.appendChild(track)
    },
    limitVideoPossibleActiveTracks() {
      this.$refs.videoTag.textTracks[0].oncuechange = function () {
        const activeCues = this.activeCues
        if (activeCues.length) {
          for (var i = 0; i < activeCues.length; i++) {
            if (i !== activeCues.length - 1) {
              activeCues[i].line = 0
              activeCues[i].position = 0
            } else {
              activeCues[i].line = 'auto'
              activeCues[i].position = 'auto'
            }
          }
        }
      }
    },
    createSubtitleUrl(subtitleContent) {
      const blob = new Blob([subtitleContent], { type: 'text/vtt' })
      const subtitleUrl = URL.createObjectURL(blob)
      return subtitleUrl
    },
    createFileForUpload() {
      const vttString = new WebVTTSerializer().serialize(this.parsedSubtitle)
      const blob = new Blob([vttString], { type: 'text/vtt' })
      const file = new File([blob], 'example.vtt', { type: 'text/vtt' })
      return file
    },
    sortParsedSubtitles() {
      this.parsedSubtitle.sort((a, b) => {
        if (a.startTime > b.startTime) {
          return 1
        } else if (a.startTime < b.startTime) {
          return -1
        }
        return 0
      })
      this.handleReRenderVttList()
    },
    handleBlurEditField() {
      this.blockClickEvent = true
      setTimeout(() => {
        this.blockClickEvent = false
        this.handleClearClickTimeout()
        this.sortParsedSubtitles()
        this.handleUpdateSubtitleContent()
        this.drawPreviewThumbSheet()
      }, 400)
    },
    handleClearClickTimeout() {
      clearTimeout(this.clickTimeout)
      this.clickTimeout = false
    },
    handleAddToEditField(index, fromKeyboard) {
      let item = this.parsedSubtitle[index]
      if (this.blockClickEvent) return
      const multipliers = {
        hours: 60 * 60,
        minutes: 60,
        seconds: 1,
        milliseconds: 0.1,
      }
      const multiplier = multipliers[item.metadata.fieldEdit.field]
      const fields = {
        main: item.metadata.fieldEdit.index === 1 ? 'startTime' : 'endTime',
        opposite: item.metadata.fieldEdit.index !== 1 ? 'startTime' : 'endTime',
      }

      item[fields.main] += 1 * multiplier

      if (item.metadata.locked) item[fields.opposite] += 1 * multiplier

      if (!fromKeyboard) {
        this.clickTimeout = setTimeout(
          () => this.handleAddToEditField(index),
          200
        )
      } else {
        this.handleBlurEditField()
      }
    },
    handleSubFromEditField(index, fromKeyboard) {
      let item = this.parsedSubtitle[index]
      if (this.blockClickEvent) return
      const multipliers = {
        hours: 60 * 60,
        minutes: 60,
        seconds: 1,
        milliseconds: 0.1,
      }
      const multiplier = multipliers[item.metadata.fieldEdit.field]
      const fields = {
        main: item.metadata.fieldEdit.index === 1 ? 'startTime' : 'endTime',
        opposite: item.metadata.fieldEdit.index !== 1 ? 'startTime' : 'endTime',
      }

      if (item[fields.main] - 1 * multiplier >= 0) {
        item[fields.main] -= 1 * multiplier
      } else {
        item[fields.main] = 0
      }

      if (item.metadata.locked) {
        if (item[fields.opposite] - 1 * multiplier >= 0) {
          item[fields.opposite] -= 1 * multiplier
        } else {
          item[fields.opposite] = 0
        }
      }

      if (!fromKeyboard) {
        this.clickTimeout = setTimeout(
          () => this.handleSubFromEditField(index),
          200
        )
      }
    },
    handleEditField(index, target) {
      this.parsedSubtitle[index].metadata.fieldEdit.index = target
      this.handleReRenderVttList()
    },
    handleEditFieldType(index, target, type) {
      const item = this.parsedSubtitle[index]
      if (!item.metadata.fieldEdit.index) this.handleEditField(index, target)
      item.metadata.fieldEdit.field = type
    },
    handlePlaySelectedSubtitle(index) {
      this.$refs.videoTag.currentTime = this.parsedSubtitle[index].startTime
      if (this.$refs.videoTag.paused) {
        this.$refs.videoTag.play()
        this.videoProperties.paused = false
      }
    },
    handleHighlightSubtitle(index, val) {
      if (
        this.menu.some((item) => item === true) ||
        this.parsedSubtitle.some((item) => item.metadata.edit)
      )
        return
      this.parsedSubtitle.map((item) => {
        item.metadata.selected = false
        return item
      })
      this.parsedSubtitle[index].metadata.selected = val
      this.handleReRenderVttList()
    },
    handleToggleLockState(index) {
      this.parsedSubtitle[index].metadata.fieldEdit.index = 0
      this.parsedSubtitle[index].metadata.locked =
        !this.parsedSubtitle[index].metadata.locked
      this.handleReRenderVttList()
    },
    handleReRenderVttList() {
      this.parsedSubtitle = [...this.parsedSubtitle]
      this.handleUpdateTContainerHeight()
    },
    handleSetNewVideoTime(e) {
      const percent = e.offsetX / this.$refs.videoTag.offsetWidth
      const calc = percent * this.$refs.videoTag.duration
      this.$refs.videoTag.currentTime = calc
    },
    handleEnableEditContentMode(index) {
      this.parsedSubtitle[index].metadata.newText =
        this.parsedSubtitle[index].text
      this.textareaTexts[index] = String(this.parsedSubtitle[index].text)
      this.parsedSubtitle[index].metadata.edit = true
      this.parsedSubtitle[index].metadata.selected = true
      this.isTypingMode = true
      this.handleReRenderVttList()
    },
    handleDisableEditContentMode(index) {
      this.parsedSubtitle[index].metadata.edit = false
      this.parsedSubtitle[index].metadata.newText = this.textareaTexts[index]
      delete this.textareaTexts[index]
      if (
        this.parsedSubtitle[index].metadata.newText !==
        this.parsedSubtitle[index].text
      ) {
        this.parsedSubtitle[index].text =
          this.parsedSubtitle[index].metadata.newText
        this.parsedSubtitle[index].tree.children = [
          {
            type: 'text',
            value: this.parsedSubtitle[index].metadata.newText,
          },
        ]
        this.handleUpdateSubtitleContent()
      }
      this.parsedSubtitle[index].metadata.selected = false
      this.parsedSubtitle[index].metadata.newText = ''
      this.isTypingMode = false
      this.handleReRenderVttList()
    },
    strip(number) {
      return Number(parseFloat(number).toPrecision(12))
    },
    handleIncreaseDelay() {
      if (this.delayAmount < 10) {
        this.delayAmount = this.strip(this.delayAmount + 0.1)

        this.parsedSubtitle.map((item, index) => {
          const reflectedItem = this.parsedSubtitle[index]
          reflectedItem.endTime += 0.1
          reflectedItem.startTime += 0.1
        })
        this.handleUpdateSubtitleContent()
        this.drawPreviewThumbSheet()
      }
    },
    handleDecreaseDelay() {
      if (this.delayAmount - 0.1 >= 0) {
        this.delayAmount = this.strip(this.delayAmount - 0.1)

        this.parsedSubtitle.map((item, index) => {
          const reflectedItem = this.parsedSubtitle[index]
          reflectedItem.endTime -= 0.1
          reflectedItem.startTime -= 0.1
        })
        this.handleUpdateSubtitleContent()
        this.drawPreviewThumbSheet()
      }
    },
    deleteSubtitlePart(index) {
      this.menu[index] = false
      this.parsedSubtitle.splice(index, 1)
      this.drawPreviewThumbSheet()
    },
    formatTime(seconds, pattern) {
      return format(new Date(0, 0, 0, 0, 0, 0, 0).setSeconds(seconds), pattern)
    },
    setHlsPlayer() {
      this.$refs.videoTag.onended = () => {
        this.videoProperties.paused = true
      }
      this.$refs.videoTag.ontimeupdate = (data) => {
        if (this.isRunningTimeUpdate) return
        this.isRunningTimeUpdate = true
        this.activateSubtitleTrack(this.selectedSubtitleSrcLang)
        let colIndex = null
        let currentSubtitle = this.parsedSubtitle.filter((item, index) => {
          if (
            data.target.currentTime >= item.startTime &&
            data.target.currentTime < item.endTime
          ) {
            colIndex = index
            return item
          }
        })
        this.videoProperties.currentTime = data.target.currentTime
        this.videoProperties.duration = data.target.duration
        this.$refs.progressLineTimeline.$el.style.left = `${
          this.pixelsPerSecond * data.target.currentTime
        }px`
        if (Date.now() - this.lastMouseWheelEventTime > 2000) {
          document.querySelector('.container__images--wrapper').scrollTo({
            left: this.pixelsPerSecond * data.target.currentTime - 225,
            behavior: 'smooth',
          })
        }
        const progressBar = document.querySelector('.video__progress-dot')
        const progressBarRemain = document.querySelector(
          '.video__progress-bar--remaining-space'
        )
        const progressPos =
          this.videoProperties.currentTime / this.videoProperties.duration
        const calc = progressPos * 100
        progressBar.style.left = calc > 97.7 ? 97.7 : calc + '%'
        progressBarRemain.style.left = calc + '%'
        if (colIndex !== null) {
          if (currentSubtitle.length) currentSubtitle = currentSubtitle[0]

          let multiplier = 1
          let additionalSum = 0
          let timeDecreaseAmount = 0
          const currentCol = this.parsedSubtitleTableItems[colIndex]

          if (Date.now() - this.lastMouseWheelEventTime < 2000) {
            this.allowAutoScroll = false
          } else {
            this.allowAutoScroll = true
          }
          const allHeightBeforeYourItem =
            this.parsedSubtitleTableItems
              .slice(0, colIndex)
              .reduce((pval, cval) => cval.height + pval, 0) - 6 // sub padding diff
          if (colIndex && this.allowAutoScroll) {
            let y = 0
            if (colIndex !== 0) {
              y = allHeightBeforeYourItem
            }
            document.querySelector('.scroller').scrollTo(0, y)
          }
          const currentColHeight = currentCol.height + 8 // plus padding diff
          const currentColDuration =
            currentSubtitle.endTime - currentSubtitle.startTime
          if (currentColHeight) {
            multiplier = currentColHeight / currentColDuration
          }
          additionalSum = allHeightBeforeYourItem - 6 // sub padding diff
          timeDecreaseAmount = currentSubtitle.startTime
          this.$refs.progressLine.$el.style.top = `${
            (this.videoProperties.currentTime - timeDecreaseAmount) *
              multiplier +
            additionalSum
          }px`
        }

        this.isRunningTimeUpdate = false
      }

      if (Hls.isSupported()) {
        let hls = new Hls()
        hls.testBandwidth = false
        hls.startLevel = 0
        hls.loadLevel = hls.startLevel
        hls.loadSource(this.video.video_hls)

        hls.attachMedia(this.$refs.videoTag)
        hls.on(
          Hls.Events.SUBTITLE_TRACKS_UPDATED,
          function () {
            hls.subtitleTrack = -1 // Disable original subtitles from m3u8
            this.handleUpdateSubtitleContent()
          }.bind(this)
        )
      } else if (
        this.$refs.videoTag.canPlayType('application/vnd.apple.mpegurl')
      ) {
        this.$refs.videoTag.src = this.video.video_hls
        this.$refs.videoTag.addEventListener(
          'canplay',
          function () {
            this.$refs.videoTag.pause()
          }.bind(this)
        )
      }
    },
    handleUpdateSubtitleContent() {
      this.updatingSubtitleContent = true
      const _this = this

      const content = this.parsedSubtitle
      const serializedContent = new WebVTTSerializer().serialize(content)

      if (this.currentSubtitleObjectURL)
        URL.revokeObjectURL(this.currentSubtitleObjectURL)
      this.currentSubtitleObjectURL = this.createSubtitleUrl(serializedContent)

      if (this.$refs.videoTag.firstChild) {
        while (this.$refs.videoTag.firstChild) {
          this.$refs.videoTag.removeChild(this.$refs.videoTag.firstChild)
        }
      }

      const newSubtitlelabel = this.langCodes.filter(
        (item) => item.srclang === this.selectedSubtitleSrcLang
      )[0].label

      setTimeout(async () => {
        this.updatingSubtitleContent = true
        this.addCustomSubtitleTrack(
          this.currentSubtitleObjectURL,
          newSubtitlelabel,
          `${this.selectedSubtitleSrcLang}_preview`
        )
        this.deactiveAllSubtitleTracks()
        this.limitVideoPossibleActiveTracks()
        setTimeout(() => {
          _this.activateSubtitleTrack(_this.selectedSubtitleSrcLang)
        }, 150)
      }, 100)
    },
    activateSubtitleTrack(trackLabel) {
      if (this.$refs.videoTag?.textTracks) {
        for (const track of this.$refs.videoTag.textTracks) {
          if (track.language === `${trackLabel}_preview`) {
            track.mode = 'showing'
          } else {
            track.mode = 'hidden'
          }
        }
      }
      this.updatingSubtitleContent = false
    },
    deactiveAllSubtitleTracks() {
      if (this.$refs.videoTag?.textTracks) {
        for (const track of this.$refs.videoTag.textTracks) {
          track.mode = 'hidden'
        }
      }
    },
    handleTogglePlayVideo() {
      if (this.$refs.videoTag.paused) {
        this.activateSubtitleTrack(this.selectedSubtitleSrcLang)
        this.$refs.videoTag.play()
        this.videoProperties.paused = false
      } else {
        this.$refs.videoTag.pause()
        this.videoProperties.paused = true
      }
    },
    handleForwardVideo() {
      if (this.$refs.videoTag.currentTime + 5 > this.$refs.videoTag.duration) {
        this.$refs.videoTag.currentTime +=
          this.$refs.videoTag.duration - this.$refs.videoTag.currentTime
      } else {
        this.$refs.videoTag.currentTime += 5
      }
    },
    handleBackwardVideo() {
      if (this.$refs.videoTag.currentTime >= 5) {
        this.$refs.videoTag.currentTime -= 5
      } else {
        this.$refs.videoTag.currentTime = 0
      }
    },
    handleResetClose() {
      this.isTypingMode = false
      window.removeEventListener('resize', this.onResizeScreen)
      document.removeEventListener('keydown', this.handleKeyDown)
      document.removeEventListener('click', this.handleClickDOM)
      document.querySelector('HTML').style.overflow = 'auto'
      this.blockClickEvent = false
      this.handleClearClickTimeout()
      this.delayAmount = 0
      if (!this.$refs.videoTag.paused) this.handleTogglePlayVideo()
      this.currentSubtitle = ''
      this.subtitleContent = ''
    },
    getPreviewURL(index) {
      const previewThumbURL = this.video.video_hls.replace(
        'playlist.m3u8',
        `seek/_${index}.jpg`
      )
      return previewThumbURL
    },
    handleLoadPreviewThumb() {
      let previewThumbURL = []
      const previewImgQnty = Math.ceil(this.video.length / 2 / 36)

      for (let i = 0; i < previewImgQnty; i++) {
        previewThumbURL.push(this.getPreviewURL(i))
      }

      this.previewThumbURLs = previewThumbURL
    },
    checkVideoAspectRatio(width, height) {
      if (height / width === 16 / 9) {
        return '9:16'
      }
      return '16:9'
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = url
      })
    },
    async drawPreviewThumbSheet(renderCanvas = false) {
      const _this = this
      const scale = this.zoomRate
      const targetMaxWidth = 10000 // browser limits: https://jhildenbiddle.github.io/canvas-size
      const widescreen = this.videoRatio === '16:9'
      const scaleWidth = (widescreen ? 0.16 : 0.1) * scale
      const scaleHeight = (widescreen ? 0.192 : 0.1) * scale

      const originalSpriteWidth = 300
      const originalSpriteHeight = widescreen ? 168 : 537
      const spritesPerRow = 6
      const spritesPerColumn = 6

      const spriteWidth = originalSpriteWidth * scaleWidth
      const spriteHeight = originalSpriteHeight * scaleHeight

      const maxCanvasWidth = ~~(targetMaxWidth / spriteWidth) * spriteWidth

      const totalSprites = Math.ceil(this.video.length / 2)
      let totalWidth = totalSprites * spriteWidth
      // eslint-disable-next-line
      let currentCanvas
      let currentContext
      const imagesContainer = document.querySelector('.container__images')

      function createNewCanvas() {
        const canvas = document.createElement('canvas')
        canvas.width = Math.min(maxCanvasWidth, totalWidth)
        canvas.height = spriteHeight
        imagesContainer.appendChild(canvas)
        currentCanvas = canvas
        currentContext = canvas.getContext('2d')
        totalWidth -= maxCanvasWidth
      }
      if (renderCanvas) {
        imagesContainer.innerHTML = ''
        while (totalWidth > 0) {
          createNewCanvas()
          let x = 0
          this.sheets.forEach((sheet) => {
            for (let row = 0; row < spritesPerColumn; row++) {
              for (let col = 0; col < spritesPerRow; col++) {
                if (x + spriteWidth > maxCanvasWidth) {
                  createNewCanvas()
                  x = 0
                }
                currentContext.drawImage(
                  sheet,
                  col * originalSpriteWidth,
                  row * originalSpriteHeight,
                  originalSpriteWidth,
                  originalSpriteHeight,
                  x,
                  0,
                  spriteWidth,
                  spriteHeight
                )
                x += spriteWidth
              }
            }
          })
        }
      }

      const containers = {
        images:
          _this.$refs.containerImagesWrapper.querySelector(
            '.container__images'
          ),
        subtitles: _this.$refs.containerImagesWrapper.querySelector(
          '.container__subtitle-items'
        ),
        timeItems: _this.$refs.containerImagesWrapper.querySelector(
          '.container__time-items'
        ),
      }

      while (containers.subtitles.firstChild) {
        containers.subtitles.removeChild(containers.subtitles.firstChild)
      }
      while (containers.timeItems.firstChild) {
        containers.timeItems.removeChild(containers.timeItems.firstChild)
      }

      const finalContainerWidth = spriteWidth * totalSprites
      const pixelsPerSecond = finalContainerWidth / this.video.length
      _this.pixelsPerSecond = pixelsPerSecond
      if (renderCanvas) {
        this.$refs.progressLineTimeline.$el.style.left = `${
          this.pixelsPerSecond * _this.$refs.videoTag.currentTime
        }px`
      }

      for (let i = 0; i <= _this.video.length; i++) {
        if (i % 8 === 0) {
          const timeFormatted = _this.getSecondsTimeFormatted(i)

          const container = document.createElement('div')
          container.classList.add('time__stick--content')

          const timeValue = document.createElement('span')
          if (this.isLongerThanAnHour) {
            timeValue.innerHTML = `${timeFormatted[0]}:${timeFormatted[1]}:${timeFormatted[2]}`
          } else {
            timeValue.innerHTML = `${timeFormatted[1]}:${timeFormatted[2]} <small>${timeFormatted[3]}</small>`
          }

          const timeValueContainer = document.createElement('div')
          timeValueContainer.classList.add('time__value--container')
          timeValueContainer.style.left = `${i !== 0 ? -43 + 21 : 0}px`
          timeValueContainer.append(timeValue)

          container.append(timeValueContainer)

          const stickElement = document.createElement('div')
          stickElement.classList.add('time__stick')
          container.append(stickElement)

          container.style.left = `${pixelsPerSecond * i}px`
          containers.timeItems.append(container)
        } else {
          const container = document.createElement('div')
          container.classList.add('time__stick')
          container.style.left = `${pixelsPerSecond * i}px`
          containers.timeItems.append(container)
        }
      }

      _this.parsedSubtitle.forEach((item) => {
        const showTime = item.endTime - item.startTime
        const width = showTime * pixelsPerSecond
        const left = item.startTime * pixelsPerSecond
        const subItem = document.createElement('div')
        subItem.style.left = `${left}px`
        subItem.style.minWidth = `${width}px`
        containers.subtitles.appendChild(subItem)
      })
    },
    handleClickDOM(e) {
      if (
        this.parsedSubtitle &&
        !e.target.classList.contains('time-field') &&
        !e.target.classList.contains('mdi-triangle') &&
        !e.target.classList.contains('mdi-triangle-down')
      ) {
        const someActive = this.parsedSubtitle.filter(
          (item) => item.metadata.fieldEdit.index
        )
        if (someActive.length) {
          someActive[0].metadata.fieldEdit.index = 0
        }
      }
    },
    handleCheckOpen(data) {
      const _this = this
      document.querySelector('HTML').style.overflow = 'hidden'
      this.videoRatio = this.checkVideoAspectRatio(
        this.video.width,
        this.video.height
      )
      this.currentSubtitle = data
      this.handleCheckDownloadSubContent(this.afterDownloadSubtitle)
      this.updateSubtitleStyles(
        this.profile.colors.captionsColor,
        this.profile.colors.captionsBackgroundColor
      )
      document.addEventListener('click', this.handleClickDOM)
      this.setShortcutEvents()
      window.addEventListener('resize', this.onResizeScreen)
      setTimeout(() => {
        document.addEventListener('wheel', function () {
          _this.lastMouseWheelEventTime = Date.now()
        })
        this.handleUpdateTContainerHeight()
      }, 400)
    },
    onResizeScreen() {
      this.handleUpdateTContainerHeight()
    },
    handleUpdateTContainerHeight() {
      const container = document.querySelector('.container__vtt-list')
      if (!container) {
        return setTimeout(() => {
          this.handleUpdateTContainerHeight()
        }, 500)
      }
      const styleId = 'table-customs'

      let styleTag = document.getElementById(styleId)

      if (!styleTag) {
        styleTag = document.createElement('style')
        styleTag.id = styleId
        document.head.appendChild(styleTag)
      }
      styleTag.textContent = `
            .t-container {
              min-height: ${
                container.getBoundingClientRect().height - 16
              }px !important;
            }
        `
    },
    selectSub(op) {
      let selectedIndex = op === 'minus' ? 1 : 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.parsedSubtitle.map((item) => {
        item.metadata.selected = false
        return item
      })
      let parsedIndex = 0
      if (op === 'minus') {
        parsedIndex =
          selectedIndex - 1 < 0
            ? this.parsedSubtitle.length - 1
            : selectedIndex - 1
      } else {
        parsedIndex =
          selectedIndex + 1 > this.parsedSubtitle.length - 1
            ? 0
            : selectedIndex + 1
      }

      const container = document.querySelector(
        '.virtual-scroller .item-container'
      )
      const containerCols = Array.from(
        container.querySelectorAll('.item-linel')
      )
      const currentCol = containerCols[parsedIndex]
      currentCol.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      })
      this.parsedSubtitle[parsedIndex].metadata.selected = true
      this.handleReRenderVttList()
    },
    selectNextSub() {
      this.selectSub('plus')
    },
    selectPreviousSub() {
      this.selectSub('minus')
    },
    editCurrentSub() {
      if (!this.isTypingMode) {
        let selectedIndex = 0
        this.parsedSubtitle.forEach((item, index) => {
          if (item.metadata.selected) selectedIndex = index
        })
        this.handleEnableEditContentMode(selectedIndex)
      } else {
        let selectedIndex = 0
        this.parsedSubtitle.forEach((item, index) => {
          if (item.metadata.edit) selectedIndex = index
        })
        this.handleDisableEditContentMode(selectedIndex)
      }
    },
    duplicateCurrentSub() {
      let selectedIndex = 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.duplicateSub(selectedIndex)
    },
    addToJoinSelectedSub() {
      let selectedIndex = 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.parsedSubtitle[selectedIndex].metadata.locked = false
      this.parsedSubtitle[selectedIndex].metadata.fieldEdit.index = 2
      this.handleAddToEditField(selectedIndex, true)
    },
    subFromJoinSelectedSub() {
      let selectedIndex = 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.parsedSubtitle[selectedIndex].metadata.locked = false
      this.parsedSubtitle[selectedIndex].metadata.fieldEdit.index = 2
      this.handleSubFromEditField(selectedIndex, true)
    },
    addToLeftSelectedSub() {
      let selectedIndex = 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.parsedSubtitle[selectedIndex].metadata.locked = false
      this.parsedSubtitle[selectedIndex].metadata.fieldEdit.index = 1
      this.handleAddToEditField(selectedIndex, true)
    },
    subFromLeftSelectedSub() {
      let selectedIndex = 0
      this.parsedSubtitle.forEach((item, index) => {
        if (item.metadata.selected) selectedIndex = index
      })
      this.parsedSubtitle[selectedIndex].metadata.locked = false
      this.parsedSubtitle[selectedIndex].metadata.fieldEdit.index = 1
      this.handleSubFromEditField(selectedIndex, true)
    },
    handleKeyDown(e) {
      const _this = this
      const keyEvents = {
        KeyL: _this.handleIncreaseDelay,
        KeyK: _this.handleDecreaseDelay,
        ArrowLeft: _this.handleBackwardVideo,
        ArrowRight: _this.handleForwardVideo,
        ArrowDown: _this.selectNextSub,
        ArrowUp: _this.selectPreviousSub,
        ShiftArrowUp: _this.addToJoinSelectedSub,
        ShiftArrowDown: _this.subFromJoinSelectedSub,
        CtrlArrowUp: _this.addToLeftSelectedSub,
        CtrlArrowDown: _this.subFromLeftSelectedSub,
        Space: _this.handleTogglePlayVideo,
        Enter: _this.editCurrentSub,
        ShiftEnter: _this.duplicateCurrentSub,
      }
      if (!this.isTypingMode) {
        if (keyEvents.hasOwnProperty(e.code)) {
          if (e.code === 'Enter' && e.shiftKey) {
            keyEvents['ShiftEnter']()
          } else if (
            (e.code === 'ArrowDown' || e.code === 'ArrowUp') &&
            (e.ctrlKey || e.shiftKey)
          ) {
            const jokerKey = e.shiftKey ? 'Shift' : 'Ctrl'
            keyEvents[`${jokerKey}${e.code}`]()
          } else if (
            (e.code === 'KeyL' || e.code === 'KeyK') &&
            this.updatingSubtitleContent
          ) {
            return
          } else {
            keyEvents[e.code]()
          }
        }
      }
    },
    setShortcutEvents() {
      document.addEventListener('keydown', this.handleKeyDown)
    },
    async afterDownloadSubtitle() {
      this.handleLoadPreviewThumb()
      this.sheets = await Promise.all(
        this.previewThumbURLs.map((url) => this.loadImage(url))
      )
      this.drawPreviewThumbSheet(true)
      this.setHlsPlayer()
    },
    async handleCheckDownloadSubContent(afterDownloadHook) {
      if (this.selectedSubtitleSrcLang) {
        await this.handleOpenSubtitleContent(this.selectedSubtitleSrcLang)
        this.setSubtitleContent()
        if (afterDownloadHook) afterDownloadHook()
      }
    },
    getSecondsTimeFormatted(sec) {
      const decimal = (sec % 1) * 1000
      return [
        format(new Date(0, 0, 0, 0, 0, sec, decimal), 'HH'),
        format(new Date(0, 0, 0, 0, 0, sec, decimal), 'mm'),
        format(new Date(0, 0, 0, 0, 0, sec, decimal), 'ss'),
        format(new Date(0, 0, 0, 0, 0, sec, decimal), 'SS'),
      ]
    },
    async handleOpenSubtitleContent(srclang) {
      const downloadURL = this.getSubtitleDownloadLink(srclang)
      const { data: subtitleContent } = await axios.get(
        `${downloadURL}?nocache=${Date.now()}`
      )
      this.subtitleContent = subtitleContent
    },
    getSubtitleDownloadLink(srclang) {
      const isStaging = process.env.VUE_APP_ENV !== 'production'
      const downloadURL = `https://subtitles${
        isStaging ? '-staging' : ''
      }.tv.pandavideo.com.br/${this.video.pullzone_name}/${
        this.video.video_external_id
      }/${srclang}.vtt`
      return downloadURL
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';

.progress-line {
  transition: top 0.1s linear;
}

.progress-line.vertical {
  transition: left 0.11s linear;
}

.subtitle-item {
  &:hover {
    @extend .selected-item;

    .time-field {
      color: var(--v-brand-base);
    }

    .delete-subtitle {
      color: var(--v-brand-lighten1) !important;
    }

    .play-subtitle {
      color: var(--v-brand-base) !important;
    }

    .duplicate-subtitle {
      display: block !important;
    }
  }
}

.scroll--stylized {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--v-brand-lighten2);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--v-neutral-lighten1);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--v-neutral-lighten1);
  }
}

.v-menu {
  &__tooltip {
    box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
    padding: 16px;
    background: white;

    .v-card {
      box-shadow: none;
    }
  }
}

.progress-line {
  position: absolute;
  z-index: 2;
}

.container {
  &__shortcut-key {
    border-radius: 4px;
    border: 2px solid var(--v-neutral-lighten1);
    display: flex;
    padding: 4px 12px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: fit-content;

    color: var(--v-neutral-lighten1);
    font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;

    i {
      color: var(--v-neutral-lighten1) !important;
      font-size: 16px;
    }
  }

  &__timeline {
    border-radius: 4px;
    border: 1px solid #e6e8f8;
  }

  &__shortcuts {
    height: 100%;
    border-radius: 4px;
    overflow-y: auto;
    border: 1px solid #e6e8f8;
    background-color: var(--v-brand-lighten3);
    padding: 0px 16px 0px 16px;

    .header {
      position: sticky;
      top: 0px;
      background-color: var(--v-brand-lighten3);
      padding-bottom: 16px;
      padding-top: 16px;
      z-index: 2;
    }

    .list__shortcuts {
      .col {
        border-bottom: 1px solid var(--v-neutral-lighten3);
        padding-bottom: 12px;
        padding-top: 12px;

        > span {
          vertical-align: sub;
        }
      }
      .col:nth-child(odd) {
        display: flex;
        text-transform: capitalize;

        .container__shortcut-key:not(:first-child):not(i
            ~ .container__shortcut-key) {
          margin-left: 8px;
        }
      }
      .col:nth-child(1),
      .col:nth-child(2) {
        padding-top: 0;
      }
    }
  }

  &__player-side {
    width: 484px;
    display: flex;
    flex-direction: column;
    max-height: inherit;
  }

  &__wrapper {
    max-height: 75vh;

    &--sub {
      max-height: inherit;
    }
  }

  &__vtt-list {
    padding: 8px 0px;
    max-width: 478px;
    position: relative;
    max-height: 69vh;
    height: 100%;
    overflow-y: hidden;
    border-radius: 4px;
    border: 1px solid var(--v-neutral-lighten3);

    .selected-item {
      background: var(--v-neutral-lighten3);

      span:not(.time-field),
      .lock-btn:not(.unlocked) {
        transition: none;
        color: var(--v-brand-base) !important;
      }

      .lock-btn.unlocked {
        color: var(--v-neutral-lighten1) !important;
      }
    }

    .lock-btn::after {
      display: none;
    }

    .lock-btn.unlocked {
      transition: none;
      transform: translateX(1.3px);
    }

    .col {
      padding: 12px 8px;
    }

    .time-content {
      align-self: center;
      width: 100%;
    }
  }

  &__time-info {
    min-width: 60px;
    position: relative;

    &::before {
      content: '[';
      font-size: 24px;
      position: absolute;
      color: var(--v-neutral-lighten2);
      left: -11px;
      top: 3px;
    }
  }
}

.video {
  &__container {
    position: relative;
    padding-bottom: 52%;

    video {
      position: absolute;
      width: 100%;
      top: 0px;
      height: 100%;
      background: black;
    }
  }

  &__progress-bar {
    width: 100%;
    background: #6182e6;
    height: 4px;
    position: absolute;
    bottom: -2px;
  }

  &__progress-seek {
    position: absolute;
    left: 0;
    top: 0;
    height: 4px;
    width: 100%;
    background: transparent;
    z-index: 9;
  }

  &__progress-dot {
    width: 12px;
    height: 12px;
    position: absolute;
    background: white;
    border-radius: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.9);
    z-index: 2;
  }

  &__progress-bar--remaining-space {
    position: absolute;
    left: 0;
    top: 0;
    height: 4px;
    width: -webkit-fill-available;
    background: white;
  }
}

.container__delay {
  padding: 4px 0px;
  border-radius: 4px;
  border: 1px solid var(--v-neutral-lighten2);
  min-width: 119px;
}

.time-field__selected {
  background: var(--v-brand-lighten1);
  border-bottom: 1px solid var(--v-brand-base);
  color: white !important;
  height: 21px;
}
::v-deep .selected-item .v-text-field__edit-subtitle.theme--dark {
  textarea {
    color: black !important;
  }
}
</style>

<style lang="scss">
.data-table__content {
  border: none !important;
  overflow-x: hidden !important;

  .virtual-scroller.scroller {
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: var(--v-brand-lighten2);
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--v-neutral-lighten1);
      border-radius: 8px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--v-neutral-lighten1);
    }
  }

  .item-container {
    position: relative;
  }
}
.scroller {
  height: 400px;
}
.item-cell {
  display: unset !important;
  padding: 0 !important;
  border-bottom: none !important;
}

.item-cell-inner {
  display: unset !important;
  width: 100% !important;
  height: 100% !important;
  word-break: unset !important;
  text-align: unset !important;
  overflow: unset !important;
  text-overflow: unset !important;
}

.t-header {
  display: none;
}
canvas {
  image-rendering: optimizeSpeed; // Older versions of FF
  image-rendering: -moz-crisp-edges; // FF 6.0+
  image-rendering: -webkit-optimize-contrast; // Webkit (non standard naming)
  image-rendering: -o-crisp-edges; // OS X & Windows Opera (12.02+)
  image-rendering: crisp-edges; // Possible future browsers.
  -ms-interpolation-mode: nearest-neighbor; // IE (non standard naming)
}
.container__time-items {
  position: relative;
  height: 33px;

  div.time__stick {
    width: 1px;
    height: 8px;
    bottom: 6px;
    background: var(--v-neutral-lighten2);
    position: absolute;
  }

  div.time__stick--content {
    position: absolute;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    bottom: 0px;

    .time__value--container {
      position: absolute;
      bottom: 16px;
    }

    span {
      white-space: nowrap;
      font-family: Rubik;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      color: var(--v-neutral-lighten2);

      small {
        white-space: nowrap;
        font-family: Rubik;
        font-size: 10px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        color: var(--v-neutral-lighten2);
      }
    }

    .time__stick {
      width: 1px;
      height: 11px;
      background: var(--v-neutral-lighten2);
    }
  }
}

.container__subtitle-items {
  margin-bottom: 3px;
  position: relative;
  height: 12px;

  div {
    position: absolute;
    width: 19px;
    height: 12px;
    border-radius: 4px;
    background-color: var(--v-brand-lighten1);
  }
}
.container__images {
  display: flex;
  width: 100%;
  padding-bottom: 8px;

  &--wrapper {
    scroll-behavior: smooth;
    position: relative;
    margin: 0 16px;
    max-width: 450px;
    overflow-x: scroll;
    margin-bottom: 16px;

    &::-webkit-scrollbar {
      height: 6px;
      width: unset;
    }

    .tile {
      display: inline-block;
      background-repeat: no-repeat;
    }
  }
}

.v-application--is-ltr
  .v-textarea.v-text-field--enclosed.v-text-field__edit-subtitle
  .v-input__slot {
  padding: 0 !important;
  padding-right: 12px !important;
}
.v-application--is-ltr
  .v-textarea.v-text-field--enclosed.v-text-field__edit-subtitle
  .v-text-field__slot
  textarea {
  padding-right: 0 !important;
  padding: 2px 12px 2px 12px !important;
  margin-top: 0 !important;

  line-height: unset;
  font-size: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--v-brand-lighten2);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--v-neutral-lighten1);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--v-neutral-lighten1);
  }
}
</style>
